
import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface FAQItem {
  question: string;
  answer: string;
}

interface ProductTabsProps {
  features: string[];
  directions: string;
  ingredients: string;
  faqs: FAQItem[];
}

const ProductTabs = ({ features, directions, ingredients, faqs }: ProductTabsProps) => {
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    if (faqOpen === index) {
      setFaqOpen(null);
    } else {
      setFaqOpen(index);
    }
  };

  return (
    <Tabs defaultValue="details" className="mb-16">
      <TabsList className="grid w-full grid-cols-3 mb-8">
        <TabsTrigger value="details">Details</TabsTrigger>
        <TabsTrigger value="ingredients">Ingredients</TabsTrigger>
        <TabsTrigger value="faq">FAQ</TabsTrigger>
      </TabsList>
      <TabsContent value="details" className="space-y-4">
        <h3 className="text-xl font-semibold mb-4">Product Features</h3>
        <ul className="list-disc pl-5 space-y-2">
          {features.map((feature, index) => (
            <li key={index}>{feature}</li>
          ))}
        </ul>
        
        <h3 className="text-xl font-semibold mt-8 mb-4">How to Use</h3>
        <p>{directions}</p>
      </TabsContent>
      
      <TabsContent value="ingredients">
        <h3 className="text-xl font-semibold mb-4">Ingredients</h3>
        <p className="mb-4">All of our ingredients are sourced with quality and sustainability in mind:</p>
        <p className="bg-muted p-4 rounded-md font-mono text-sm">{ingredients}</p>
      </TabsContent>
      
      <TabsContent value="faq">
        <h3 className="text-xl font-semibold mb-4">Frequently Asked Questions</h3>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="border border-border rounded-md overflow-hidden">
              <button
                onClick={() => toggleFaq(index)}
                className="flex items-center justify-between w-full p-4 text-left font-medium hover:bg-muted/50 transition-colors"
              >
                {faq.question}
                {faqOpen === index ? (
                  <ChevronUp className="h-5 w-5 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 flex-shrink-0" />
                )}
              </button>
              {faqOpen === index && (
                <div className="p-4 pt-0 border-t border-border">
                  <p className="text-muted-foreground">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </TabsContent>
    </Tabs>
  );
};

export default ProductTabs;
